"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Search, Check, XIcon } from "lucide-react";
import {
  WERT_COUNTRIES,
  WERT_US_STATES,
  type Region,
} from "@/lib/wert-availability";

type Tab = "countries" | "us-states";

interface WertRegionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WertRegionsModal = ({ isOpen, onClose }: WertRegionsModalProps) => {
  const [activeTab, setActiveTab] = useState<Tab>("countries");
  const [searchQuery, setSearchQuery] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset search when switching tabs
  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Trap focus inside modal
  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTabKey);
    return () => document.removeEventListener("keydown", handleTabKey);
  }, [isOpen]);

  const regions = activeTab === "countries" ? WERT_COUNTRIES : WERT_US_STATES;

  const filteredRegions = useMemo(() => {
    if (!searchQuery.trim()) return regions;
    const query = searchQuery.toLowerCase();
    return regions.filter((region) =>
      region.name.toLowerCase().includes(query)
    );
  }, [regions, searchQuery]);

  if (!isOpen) return null;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal container - NO p-6 here, handle padding per section */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wert-regions-title"
        className="relative z-10 bg-[#111] border border-white/10 rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
      >
        {/* ===== HEADER - Title and X close button ===== */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 id="wert-regions-title" className="text-lg font-semibold text-white">Supported Regions</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 -mr-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ===== BODY - Controls and intro ===== */}
        <div className="px-6 pb-4">
          {/* Intro text */}
          <p className="text-gray-400 text-sm leading-relaxed mb-4">
            Card purchases are processed by Wert and aren&apos;t available in
            every region. Search below to check your country or US state.
          </p>

          {/* Tab switcher */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setActiveTab("countries")}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "countries"
                  ? "bg-[#f9cd4f]/20 text-[#f9cd4f] border border-[#f9cd4f]/30"
                  : "bg-white/5 text-gray-400 border border-transparent hover:bg-white/10"
              }`}
            >
              Countries
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("us-states")}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "us-states"
                  ? "bg-[#f9cd4f]/20 text-[#f9cd4f] border border-[#f9cd4f]/30"
                  : "bg-white/5 text-gray-400 border border-transparent hover:bg-white/10"
              }`}
            >
              US States
            </button>
          </div>

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                activeTab === "countries"
                  ? "Search countries..."
                  : "Search states..."
              }
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-[#f9cd4f]/50 focus:border-[#f9cd4f]/50"
            />
          </div>
        </div>

        {/* ===== SCROLLABLE LIST REGION ===== */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 border-t border-white/10 pt-4">
          {filteredRegions.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              No matches for &ldquo;{searchQuery}&rdquo;
            </div>
          ) : (
            <div className="space-y-1">
              {filteredRegions.map((region, idx) => (
                <RegionRow key={region.name} region={region} index={idx} />
              ))}
            </div>
          )}
        </div>

        {/* ===== FOOTER REGION (non-scrolling) ===== */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-white/10">
          <p className="text-gray-500 text-xs leading-relaxed">
            Source:{" "}
            <a
              href="https://docs.wert.io/docs/service-availability"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white underline"
            >
              Wert official availability list
            </a>
            , last updated 2026-05-24. If you&apos;re in a supported region and
            still hit issues, try Apple Pay or Google Pay, or contact your card
            issuer.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

const RegionRow = ({ region, index }: { region: Region; index: number }) => {
  const isSupported = region.status === "supported";

  return (
    <div
      className={`flex items-center justify-between py-2.5 px-3 rounded-lg ${
        index % 2 === 0 ? "bg-white/[0.02]" : ""
      }`}
    >
      <span className="text-white text-sm">{region.name}</span>
      {isSupported ? (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-400">
          <Check className="w-3 h-3" />
          Supported
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/15 text-red-400">
          <XIcon className="w-3 h-3" />
          Not available
        </span>
      )}
    </div>
  );
};

export default WertRegionsModal;
